import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, Mail, ScrollText, Lock, Fingerprint } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";
import CeoPasskeys from "../../components/admin/CeoPasskeys";

const Section = ({ title, icon: Icon, children, testid }) => (
  <div className="rounded-2xl border border-border bg-white p-6" data-testid={testid}>
    <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-gold" /><h3 className="font-serif text-lg text-navy">{title}</h3></div>
    <div className="mt-4">{children}</div>
  </div>
);

export default function SecuritySettings({ currentUser, refreshUser }) {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [ownerEmail, setOwnerEmail] = useState({ email: "", password: "" });
  const [twofa, setTwofa] = useState(null); // {qr, secret}
  const [twofaCode, setTwofaCode] = useState("");
  const [backup, setBackup] = useState(null);
  const [disablePw, setDisablePw] = useState("");

  const load = async () => {
    try {
      const reqs = [api.get("/admin/security/settings").catch(() => ({ data: null }))];
      const canAudit = currentUser?.is_owner || (currentUser?.effective_permissions?.activity || []).includes("view");
      if (canAudit) reqs.push(api.get("/admin/audit-logs?limit=50")); else reqs.push(Promise.resolve({ data: [] }));
      const [s, l] = await Promise.all(reqs);
      setSettings(s.data); setLogs(l.data);
      if (s.data?.owner_email) setOwnerEmail((o) => ({ ...o, email: s.data.owner_email }));
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changePw = async () => {
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed"); setPw({ current: "", next: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startTwofa = async () => {
    try { const { data } = await api.post("/auth/2fa/setup"); setTwofa(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const enableTwofa = async () => {
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: twofaCode });
      setBackup(data.backup_codes); setTwofa(null); setTwofaCode("");
      toast.success("Two-Factor Authentication enabled"); refreshUser?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const disableTwofa = async () => {
    try { await api.post("/auth/2fa/disable", { password: disablePw }); setDisablePw(""); toast.success("2FA disabled"); refreshUser?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeOwnerEmail = async () => {
    try { const { data } = await api.post("/admin/security/owner-email", ownerEmail); toast.success("Owner email updated"); setOwnerEmail({ ...ownerEmail, password: "" }); refreshUser?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="security-panel">
      <div>
        <h2 className="font-serif text-2xl text-navy">Security Settings</h2>
        <p className="mt-1 text-sm text-muted-foreground">Manage your credentials, two-factor authentication, and platform security. The Owner retains ultimate control.</p>
      </div>

      {settings && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-white px-4 py-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Password Policy</p><p className="mt-1 text-sm font-semibold text-navy">Min {settings.password_policy.min_length} chars · {settings.password_policy.complexity}</p></div>
          <div className="rounded-xl border border-border bg-white px-4 py-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Lockout</p><p className="mt-1 text-sm font-semibold text-navy">{settings.lockout.max_attempts} attempts → {settings.lockout.lockout_minutes} min lock</p></div>
          <div className="rounded-xl border border-border bg-white px-4 py-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Session</p><p className="mt-1 text-sm font-semibold text-navy">{settings.session_hours}h token lifetime</p></div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {currentUser?.is_owner && (
          <Section title="CEO Passkeys (WebAuthn)" icon={Fingerprint} testid="sec-ceo-passkeys">
            <CeoPasskeys />
          </Section>
        )}
        <Section title="Change My Password" icon={KeyRound} testid="sec-change-password">
          <div className="space-y-3">
            <input type="password" placeholder="Current password" className={inputCls} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="sec-current-pw" />
            <input type="password" placeholder="New password" className={inputCls} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="sec-new-pw" />
            <button onClick={changePw} className="rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="sec-change-pw-btn">Update Password</button>
          </div>
        </Section>

        <Section title="Two-Factor Authentication" icon={ShieldCheck} testid="sec-2fa">
          {currentUser?.twofa_enabled ? (
            <div className="space-y-3">
              <p className="text-sm text-success">2FA is currently <b>enabled</b> on your account.</p>
              <input type="password" placeholder="Confirm password to disable" className={inputCls} value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="sec-2fa-disable-pw" />
              <button onClick={disableTwofa} className="rounded-lg border border-destructive px-5 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/5" data-testid="sec-2fa-disable-btn">Disable 2FA</button>
            </div>
          ) : twofa ? (
            <div className="space-y-3">
              <p className="text-sm text-charcoal">Scan this QR in Google Authenticator / Authy, then enter the 6-digit code.</p>
              <img src={twofa.qr} alt="2FA QR" className="h-40 w-40 rounded-lg border border-border" data-testid="sec-2fa-qr" />
              <p className="text-xs text-muted-foreground">Manual key: <code className="text-navy">{twofa.secret}</code></p>
              <input inputMode="numeric" placeholder="123456" className={inputCls} value={twofaCode} onChange={(e) => setTwofaCode(e.target.value)} data-testid="sec-2fa-code" />
              <button onClick={enableTwofa} className="rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="sec-2fa-enable-btn">Verify &amp; Enable</button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Add an extra layer of security. Recommended for Owner &amp; Administrators.</p>
              <button onClick={startTwofa} className="rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="sec-2fa-setup-btn">Set up 2FA</button>
            </div>
          )}
          {backup && (
            <div className="mt-4 rounded-lg border border-gold/40 bg-gold/5 p-3">
              <p className="text-xs font-bold text-gold-dark">SAVE THESE BACKUP CODES (shown once):</p>
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm text-navy" data-testid="sec-2fa-backup">
                {backup.map((c) => <span key={c}>{c}</span>)}
              </div>
            </div>
          )}
        </Section>

        {currentUser?.is_owner && (
          <Section title="Owner Email (Super Admin)" icon={Mail} testid="sec-owner-email">
            <p className="mb-3 text-sm text-muted-foreground">Change the Owner login email anytime — ownership, permissions, audit logs and data are unaffected.</p>
            <div className="space-y-3">
              <input type="email" placeholder="New owner email" className={inputCls} value={ownerEmail.email} onChange={(e) => setOwnerEmail({ ...ownerEmail, email: e.target.value })} data-testid="sec-owner-email-input" />
              <input type="password" placeholder="Confirm with your password" className={inputCls} value={ownerEmail.password} onChange={(e) => setOwnerEmail({ ...ownerEmail, password: e.target.value })} data-testid="sec-owner-email-pw" />
              <button onClick={changeOwnerEmail} className="rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="sec-owner-email-btn">Update Owner Email</button>
            </div>
          </Section>
        )}
      </div>

      <Section title="Security Audit Log" icon={ScrollText} testid="sec-audit">
        {logs.length === 0 ? <p className="text-sm text-muted-foreground">No audit events visible for your role.</p> : (
          <div className="max-h-96 overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead><tr className="bg-navy/[0.03] text-left text-xs uppercase tracking-wider text-charcoal/60"><th className="px-3 py-2">Action</th><th className="px-3 py-2">Module</th><th className="px-3 py-2">Actor</th><th className="px-3 py-2">When</th></tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-border/60" data-testid={`audit-row-${l.id}`}>
                    <td className="px-3 py-2 font-semibold text-navy">{l.action}</td>
                    <td className="px-3 py-2 text-charcoal/80">{l.module}</td>
                    <td className="px-3 py-2 text-charcoal/80">{l.actor_email || "—"}</td>
                    <td className="px-3 py-2 text-xs text-charcoal/50">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
